import * as THREE from 'three'

import { ENV } from '../config/constants.js'
import { DEG } from '../util/units.js'

/**
 * Sky.
 *
 * A gradient dome plus a sun disc and a little atmospheric scattering around
 * it. Not a physical sky model — it does not need to be. What it does need is
 * a believable horizon line, because at 600 feet on the downwind the horizon
 * is the only pitch reference you have.
 */

const vertexShader = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    // Translation is stripped so the dome is locked to the camera.
    vec4 pos = projectionMatrix * mat4(mat3(modelViewMatrix)) * vec4(position, 1.0);
    gl_Position = pos.xyww;
  }
`

const fragmentShader = /* glsl */ `
  uniform vec3 uSunDir;
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  uniform vec3 uGround;
  uniform vec3 uSunColor;

  varying vec3 vDir;

  vec3 toSRGB(vec3 c) {
    return mix(c * 12.92,
               1.055 * pow(max(c, vec3(0.0)), vec3(0.41666)) - 0.055,
               step(vec3(0.0031308), c));
  }

  void main() {
    vec3 d = normalize(vDir);
    float h = d.y;

    // Sky gradient, with a tight compression near the horizon so the line
    // reads crisply rather than as a soft smear.
    float t = clamp(pow(max(h, 0.0), 0.42), 0.0, 1.0);
    vec3 col = mix(uHorizon, uZenith, t);

    // Below the horizon the dome takes the haze colour so there is no visible
    // seam where it meets the distant sea disc.
    col = mix(col, uGround, clamp(-h * 6.0, 0.0, 1.0));

    // Sun disc plus glow.
    float cosAngle = dot(d, normalize(uSunDir));
    float disc = smoothstep(0.99955, 0.99985, cosAngle);
    float glow = pow(max(cosAngle, 0.0), 320.0) * 0.7
               + pow(max(cosAngle, 0.0), 22.0) * 0.16;
    col += uSunColor * (disc * 8.0 + glow);

    // Match the renderer's tone mapping so the dome and the fog-coloured sea
    // disc meet without a brightness step at the horizon.
    #ifdef TONE_MAPPING
      col = toneMapping(col);
    #endif

    gl_FragColor = vec4(toSRGB(col), 1.0);
  }
`

export class SkyDome {
  constructor(sunDirection) {
    this.uniforms = {
      uSunDir: { value: sunDirection.clone().normalize() },
      uZenith: { value: new THREE.Color(0x1d4d84) },
      uHorizon: { value: new THREE.Color(0xa8c4d8) },
      uGround: { value: new THREE.Color(0x8fadc0) },
      uSunColor: { value: new THREE.Color(0xfff2d6) },
    }

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader,
      fragmentShader,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
    })

    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 32), this.material)
    this.mesh.frustumCulled = false
    this.mesh.renderOrder = -1000
    this.mesh.name = 'SkyDome'
  }

  /** Colour of the haze at the horizon — the scene fog should match it. */
  get hazeColor() {
    return this.uniforms.uHorizon.value.clone()
  }

  dispose() {
    this.mesh.geometry.dispose()
    this.material.dispose()
  }
}

/** Unit vector pointing from the scene toward the sun. */
export function sunDirectionFromEnv() {
  const el = ENV.SUN_ELEVATION_DEG * DEG
  const az = ENV.SUN_AZIMUTH_DEG * DEG
  // Azimuth is clockwise from north; world north is -Z.
  return new THREE.Vector3(
    Math.cos(el) * Math.sin(az),
    Math.sin(el),
    -Math.cos(el) * Math.cos(az),
  ).normalize()
}
